import React, { useEffect, useState } from "react";

export default function About() {
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => {
    if (!photoOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPhotoOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [photoOpen]);

  const linkedInUrl =
    "https://www.linkedin.com/in/shivam-chaudhary-46791a420/";

  // NO JSON-LD ON THIS PAGE, DELIBERATELY.
  //
  // This page used to emit its own @graph describing the same three
  // real-world things the static pages describe -- but under different
  // @ids: the Person as /about#shivam-chaudhary rather than
  // /founder#person, and GridMind as /about#gridmind rather than
  // /gridmind#software. Two @ids for one thing is two entities as far as
  // a consumer is concerned, so the graphs competed instead of
  // reinforcing each other. It also gave the Person a url of /about,
  // which 301s to /founder, and gave GridMind a url of the site root.
  //
  // The canonical graph lives in scripts/postbuild-seo.js and is served
  // on the static / and /founder pages. Keep it there and nowhere else.

  return (
    <>
      <main
        style={{
          maxWidth: 900,
          margin: "0 auto",
          padding: "64px 24px 100px",
          fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          color: "#eaf4ed",
          lineHeight: 1.7,
        }}
      >
        {/* HERO */}

        <header style={{ marginBottom: 40 }}>
          <p
            style={{
              margin: "0 0 10px",
              color: "#00dc64",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            Grid Intelligence · Evidence First
          </p>

          <h1
            style={{
              margin: 0,
              color: "#ffffff",
              fontSize: "clamp(36px, 7vw, 64px)",
              lineHeight: 1.05,
              letterSpacing: "-1.5px",
            }}
          >
            About GridMind
          </h1>

          <p
            style={{
              maxWidth: 700,
              margin: "18px 0 0",
              color: "#91a497",
              fontSize: 18,
            }}
          >
            Trustworthy grid intelligence for electricity distribution
            systems, with an emphasis on evidence, confidence and
            verifiable results.
          </p>
        </header>

        {/* ABOUT GRIDMIND */}

        <section style={{ marginTop: 38 }}>
          <p>
            GridMind is an AI-powered operational intelligence platform for
            electricity distribution and power-grid systems, developed and
            operated by IntelliLearn AI Private Limited. It analyses
            electricity-feeder data to assess transformer health, detect
            faults, support grid monitoring and measure energy savings from
            grid interventions.
          </p>

          <p>
            The platform is designed around an evidence-first principle:
            systems should state their own confidence, distinguish measured
            data from estimates and avoid making claims that available
            evidence cannot support.
          </p>

          <p>
            When available data cannot justify a precise conclusion,
            GridMind is designed to communicate that uncertainty rather than
            present a number that only appears authoritative.
          </p>
        </section>

        {/* COMPANY */}

        <section
          style={{
            marginTop: 48,
            padding: "30px",
            background:
              "linear-gradient(145deg, rgba(0,220,100,0.07), rgba(255,255,255,0.025))",
            border: "1px solid rgba(0,220,100,0.15)",
            borderRadius: 18,
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              color: "#00dc64",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "1.8px",
              textTransform: "uppercase",
            }}
          >
            Company
          </p>

          <h2
            style={{
              margin: "0 0 14px",
              color: "#ffffff",
              fontSize: 26,
            }}
          >
            IntelliLearn AI Private Limited
          </h2>

          <p
            style={{
              margin: 0,
              color: "#b7c6bb",
            }}
          >
            IntelliLearn AI Private Limited is an Indian AI and
            deep-technology company incorporated in 2026 and based in
            Sitapur, Uttar Pradesh, India. The company develops technology
            platforms across energy and education, including GridMind.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 20,
            }}
          >
            {[
              "DPIIT Recognised",
              "Deep Technology",
              "Energy Intelligence",
              "India",
            ].map((item) => (
              <span
                key={item}
                style={{
                  padding: "7px 11px",
                  borderRadius: 999,
                  background: "rgba(0,220,100,0.08)",
                  border: "1px solid rgba(0,220,100,0.16)",
                  color: "#9ee8b8",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </section>

        {/* FOUNDER */}

        <section
          style={{
            marginTop: 48,
            padding: "32px",
            background: "#0b1510",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 18,
            display: "flex",
            gap: 28,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => setPhotoOpen(true)}
            aria-label="Enlarge official portrait of Shivam Chaudhary"
            style={{
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "zoom-in",
              flexShrink: 0,
            }}
          >
            <img
              src="/shivam-chaudhary.jpg"
              alt="Shivam Chaudhary, Founder and CEO of IntelliLearn AI Private Limited and founder of GridMind"
              width="144"
              height="144"
              loading="eager"
              style={{
                width: 144,
                height: 144,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid #00dc64",
                display: "block",
              }}
            />
          </button>

          <div
            style={{
              flex: 1,
              minWidth: 240,
            }}
          >
            <p
              style={{
                margin: "0 0 5px",
                color: "#00dc64",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "1.8px",
                textTransform: "uppercase",
              }}
            >
              Founder
            </p>

            <h2
              style={{
                margin: 0,
                color: "#ffffff",
                fontSize: 27,
              }}
            >
              Shivam Chaudhary
            </h2>

            <p
              style={{
                margin: "4px 0 14px",
                color: "#00dc64",
                fontWeight: 650,
              }}
            >
              Founder &amp; CEO · IntelliLearn AI Private Limited
            </p>

            <p
              style={{
                margin: 0,
                color: "#b7c6bb",
              }}
            >
              Shivam Chaudhary is the Founder and CEO of IntelliLearn AI
              Private Limited, an Indian AI and deep-technology company
              incorporated in 2026. He founded IntelliLearn AI and leads the
              development of its technology platforms across energy and
              education. He is also the founder of GridMind, IntelliLearn
              AI&apos;s AI-powered operational intelligence platform for
              electricity distribution and power-grid systems. GridMind is
              developed and operated by IntelliLearn AI Private Limited.
            </p>

            <p
              style={{
                margin: "14px 0 0",
                color: "#75877b",
                fontSize: 14,
              }}
            >
              Based in Sitapur, Uttar Pradesh, India
            </p>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginTop: 14,
                fontSize: 14,
              }}
            >
              <a
                href="mailto:tech@intellilearnai.com"
                style={{
                  color: "#00dc64",
                  textDecoration: "none",
                }}
              >
                tech@intellilearnai.com
              </a>

              <a
                href={linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#00dc64",
                  textDecoration: "none",
                }}
              >
                LinkedIn
              </a>
            </div>
          </div>
        </section>

        {/* LEGAL / ENTITY DETAILS */}

        <section
          style={{
            marginTop: 48,
            paddingTop: 30,
            borderTop: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          <p
            style={{
              margin: 0,
              color: "#75877b",
              fontSize: 14,
            }}
          >
            IntelliLearn AI Private Limited · Sitapur, Uttar Pradesh,
            India · CIN U63119UP2026PTC248843
          </p>
        </section>
      </main>

      {/* ENLARGED FOUNDER PHOTO */}

      {photoOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged official portrait of Shivam Chaudhary"
          onClick={() => setPhotoOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0, 0, 0, 0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            cursor: "zoom-out",
          }}
        >
          <button
            type="button"
            onClick={() => setPhotoOpen(false)}
            aria-label="Close enlarged photo"
            style={{
              position: "absolute",
              top: 20,
              right: 24,
              width: 44,
              height: 44,
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(255,255,255,0.08)",
              color: "#ffffff",
              fontSize: 25,
              cursor: "pointer",
            }}
          >
            ×
          </button>

          <img
            src="/shivam-chaudhary.jpg"
            alt="Shivam Chaudhary, Founder and CEO of IntelliLearn AI Private Limited and founder of GridMind"
            onClick={(event) => event.stopPropagation()}
            style={{
              maxWidth: "min(90vw, 720px)",
              maxHeight: "88vh",
              objectFit: "contain",
              borderRadius: 16,
              boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
              cursor: "default",
            }}
          />
        </div>
      )}
    </>
  );
}